import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import '../../styles/admin-dashboard.css';
import { IcoCheck, IcoTrash } from '../dashboard/DashIcons';


function EditEmployee() {
  const { isSuperAdmin, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'employee',
    employmentType: '',
    hourlyRate: '0',
    startDate: '',
    active: true
  });

  useEffect(() => {
    if (!id) return;
    const token          = user?.token;
    const authHeaders    = { 'Authorization': `Bearer ${token}` };
    const organizationId = user?.organizationId;

    const fetchEmployee = async () => {
      try {
        const res  = await fetch(`/api/employees/${id}`, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          if (!isSuperAdmin() && data.role === 'superadmin') { navigate('/team'); return; }
          setFormData({
            firstName:      data.firstName      || '',
            lastName:       data.lastName       || '',
            email:          data.email          || '',
            role:           data.role           || 'employee',
            employmentType: data.employmentType || '',
            hourlyRate:     data.hourlyRate?.toString() || '0',
            startDate:      data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '',
            active:         data.active !== undefined ? data.active : true,
          });
          setSelectedDepartments(
            (data.departmentIds || []).map(d => (typeof d === 'object' ? d._id || d.toString() : d))
          );
          setSelectedManagers(
            (data.managerIds || []).map(m => (typeof m === 'object' ? m._id || m.toString() : m))
          );
        } else {
          setError('Failed to load employee');
          setTimeout(() => navigate('/team'), 2000);
        }
      } catch (e) { console.error(e); setError('Error loading employee'); }
      finally { setLoading(false); }
    };

    const fetchDepartments = async () => {
      try {
        const res = await fetch(`/api/departments?organizationId=${organizationId}`, { headers: authHeaders });
        if (res.ok) setDepartments(await res.json());
      } catch (e) { console.error(e); }
    };

    const fetchManagers = async () => {
      try {
        const res  = await fetch('/api/staff?role=manager', { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.employees || [];
          setManagers(list.filter(m => m.role === 'manager'));
        }
      } catch (e) { console.error(e); }
    };

    fetchEmployee();
    fetchDepartments();
    fetchManagers();
  }, [id, navigate, isSuperAdmin, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;

      const response = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          hourlyRate:    Math.round(parseFloat(formData.hourlyRate) * 100) / 100 || 0,
          departmentIds: selectedDepartments,
          managerIds:    selectedManagers,
        })
      });

      if (response.ok) {
        setSuccess('Employee updated successfully!');
        setTimeout(() => navigate(`/employee/${id}`), 1500);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update employee');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      setError('Error updating employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const msg = isSuperAdmin()
      ? 'Are you sure you want to permanently delete this employee? This cannot be undone.'
      : 'Are you sure you want to delete this employee?';
    if (!window.confirm(msg)) return;

    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;

      const url = isSuperAdmin() ? `/api/employees/${id}?permanent=true` : `/api/employees/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        navigate('/team');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      setError('Error deleting employee');
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-content">
          <p style={{ textAlign: 'center', padding: '3rem', fontSize: '1.25rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <button
          onClick={() => navigate(`/employee/${id}`)}
          style={{ marginBottom: '1.5rem', padding: '0.5rem 1rem', background: '#9ca3af', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}
        >
          ← Back to Profile
        </button>

        {error && (
          <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ padding: '1rem', background: '#dcfce7', color: '#166534', borderRadius: '8px', marginBottom: '1.5rem' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '1.5rem' }}>

            {/* Basic Information */}
            <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem' }}>Basic Information</h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }} />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }} />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Email {['manager', 'adminassistant', 'admin', 'superadmin'].includes(formData.role) ? '*' : '(optional)'}
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange}
                    required={['manager', 'adminassistant', 'superadmin'].includes(formData.role)}
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }} />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Role *</label>
                  <select name="role" value={formData.role} onChange={handleChange}
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }}>
                    <option value="employee">Employee</option>
                    <option value="teamlead">Team Lead</option>
                    <option value="manager">Manager</option>
                    <option value="adminassistant">Assistant Admin</option>
                    {isSuperAdmin() && <option value="admin">Admin</option>}
                    {isSuperAdmin() && <option value="superadmin">Super Admin</option>}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Employment Type</label>
                  <select name="employmentType" value={formData.employmentType} onChange={handleChange}
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }}>
                    <option value="">Select one</option>
                    <option value="fulltime">Full-time</option>
                    <option value="parttime">Part-time</option>
                    <option value="salaried">Salaried</option>
                  </select>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.4rem' }}>
                    Determines PTO &amp; sick time allocation
                  </p>
                </div>

                {/* Assign Managers */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Assigned Managers
                    {selectedManagers.length > 0 && (
                      <span style={{ marginLeft: '0.5rem', color: '#22c55e', fontWeight: '600', fontSize: '0.8rem' }}>
                        • {selectedManagers.length} selected
                      </span>
                    )}
                  </label>
                  {managers.length === 0 ? (
                    <p style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                      No managers found in your organization.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {managers.map(m => {
                        const selected = selectedManagers.includes(m._id);
                        return (
                          <button
                            key={m._id}
                            type="button"
                            onClick={() => setSelectedManagers(prev =>
                              prev.includes(m._id) ? prev.filter(x => x !== m._id) : [...prev, m._id]
                            )}
                            style={{
                              padding: '0.35rem 0.75rem', borderRadius: '99px',
                              border: selected ? '2px solid #22c55e' : '2px solid #e5e7eb',
                              background: selected ? '#ede9fe' : '#f9fafb',
                              color: selected ? '#4c1d95' : '#374151',
                              fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '0.35rem',
                            }}
                          >
                            {selected && <IcoCheck size={12} color="#22c55e"/>}
                            {m.firstName} {m.lastName}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Departments */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Departments (Optional)</label>
                  {departments.length === 0 ? (
                    <p style={{ padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                      No departments available.
                    </p>
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {departments.map(dept => {
                          const selected = selectedDepartments.includes(dept._id);
                          return (
                            <button
                              key={dept._id}
                              type="button"
                              onClick={() => setSelectedDepartments(prev =>
                                prev.includes(dept._id) ? prev.filter(d => d !== dept._id) : [...prev, dept._id]
                              )}
                              style={{
                                padding: '0.35rem 0.75rem', borderRadius: '99px',
                                border: selected ? `2px solid ${dept.color}` : '2px solid #e5e7eb',
                                background: selected ? dept.color : '#f9fafb',
                                color: selected ? 'white' : '#374151',
                                fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                              }}
                            >
                              {selected && <IcoCheck size={12} color="#22c55e"/>}
                              {dept.name}
                            </button>
                          );
                        })}
                      </div>
                      {selectedDepartments.length > 0 && (
                        <p style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '600', margin: '0.4rem 0 0' }}>
                          {selectedDepartments.length} department{selectedDepartments.length !== 1 ? 's' : ''} selected
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pay Options — superadmin + manager */}
            {(isSuperAdmin() || user?.role === 'manager') && (
              <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem' }}>Pay Options</h2>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Regular Hourly Rate ($/hr)</label>
                  <input type="number" name="hourlyRate" value={formData.hourlyRate} onChange={handleChange} min="0" step="0.01"
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }} />
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>Base hourly rate for regular hours</p>
                </div>
              </div>
            )}

            {/* General Settings */}
            <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem' }}>General Settings</h2>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Hire Date</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange}
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }} />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" name="active" checked={formData.active} onChange={handleChange}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                    <span style={{ fontWeight: '600' }}>Active Employee</span>
                  </label>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem', marginLeft: '1.75rem' }}>
                    Uncheck to deactivate this employee
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
              {(isSuperAdmin() || user?.role === 'manager') && (
                <button type="button" onClick={handleDelete}
                  style={{ padding: '0.75rem 1.5rem', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                  <><IcoTrash size={14}/> Delete Employee</>
                </button>
              )}
              <div style={{ display: 'flex', gap: '1rem', marginLeft: 'auto' }}>
                <button type="button" onClick={() => navigate(`/employee/${id}`)}
                  style={{ padding: '0.75rem 1.5rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '0.75rem 1.5rem', background: saving ? '#9ca3af' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEmployee;